<template>
  <basic-page title="Kemikaalitiedot (VAK / TOKEVA)">
    <v-tabs v-model="activeTab" background-color="transparent" class="mb-4">
      <v-tab>
        <v-icon left>mdi-flask</v-icon>
        Kemikaalihaku
      </v-tab>
      <v-tab>
        <v-icon left>mdi-gas-cylinder</v-icon>
        Kaasupullojen värit
      </v-tab>
      <v-tab>
        <v-icon left>mdi-book-open-page-variant</v-icon>
        Kaikki T-toimintaohjeet
      </v-tab>
      <v-tab>
        <v-icon left>mdi-numeric</v-icon>
        Vaaran tunnusnumerot
      </v-tab>
      <v-tab>
        <v-icon left>mdi-alert-octagon</v-icon>
        Varoituslipukkeet
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="transparent-tabs-items" touchless>
      <!-- TAB 1: KEMIKAALIHAKU & TOKEVA T-OHJE -->
      <v-tab-item>
        <v-row>
          <!-- Search & Placard Input -->
          <v-col cols="12" md="5" lg="4">
            <v-card outlined class="mb-4">
              <v-card-title class="py-2 text-subtitle-1 font-weight-bold d-flex justify-space-between align-center">
                <span>VAK / ADR -haku</span>
                <input
                  ref="vakCameraInput"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style="display: none"
                  @change="onVakImageCaptured"
                />
                <v-btn
                  icon
                  small
                  color="primary"
                  :loading="ocrLoading"
                  title="Ota kuva oranssista VAK-kilvestä kameralla"
                  @click="$refs.vakCameraInput.click()"
                >
                  <v-icon>mdi-camera</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text>
                <!-- Visual Orange Placard Input -->
                <div class="vak-placard-container mb-4">
                  <div class="vak-placard">
                    <div class="vak-row top-row">
                      <input
                        v-model="searchDangerCode"
                        type="text"
                        placeholder="Kemler (esim. 80 tai 33)"
                        maxlength="6"
                        class="vak-input text-uppercase font-weight-bold"
                        @input="onPlacardInput"
                      />
                    </div>
                    <div class="vak-divider" />
                    <div class="vak-row bottom-row">
                      <input
                        v-model="searchUnCode"
                        type="text"
                        placeholder="YK-nro (esim. 1830 tai 1203)"
                        maxlength="4"
                        class="vak-input font-weight-bold"
                        @input="onPlacardInput"
                      />
                    </div>
                  </div>
                  <div class="d-flex align-center justify-space-between mt-1 px-1" style="max-width: 320px; width: 100%;">
                    <span class="caption grey--text">
                      Syötä kilven nro ylös/alas
                    </span>
                    <v-btn
                      v-if="searchDangerCode || searchUnCode"
                      x-small
                      text
                      color="error"
                      class="px-1"
                      @click="clearPlacard"
                    >
                      <v-icon left x-small>mdi-close</v-icon>
                      Tyhjennä kilpi
                    </v-btn>
                  </div>
                </div>

                <!-- Free Text Search Field -->
                <v-text-field
                  v-model="searchQuery"
                  label="Etsi kemikaalin nimellä tai YK-numerolla"
                  placeholder="esim. bensiini, rikkihappo, 1830..."
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  hide-details="auto"
                  class="mb-3"
                  @click:clear="resetSearch"
                  @input="onTextSearchInput"
                />

                <!-- Active Warning Placard Filter Banner -->
                <v-alert
                  v-if="searchFlagCode"
                  dense
                  outlined
                  color="orange darken-3"
                  class="mb-3"
                  dismissible
                  @input="clearFlagFilter"
                >
                  <div class="d-flex align-center justify-space-between flex-wrap">
                    <div class="caption font-weight-bold">
                      Suodatettu: Lipuke {{ searchFlagCode }} ({{ filteredChemicals.length }} kpl)
                    </div>
                    <v-btn x-small text color="primary" @click="clearFlagFilter">
                      Poista
                    </v-btn>
                  </div>
                </v-alert>

                <!-- Quick Common Chemicals Pills -->
                <div class="mb-3">
                  <div class="subtitle-2 font-weight-bold grey--text text--darken-2 mb-1">
                    Yleisimmät kemikaalit:
                  </div>
                  <v-chip-group column>
                    <v-chip
                      v-for="quick in quickChemicals"
                      :key="quick.un"
                      outlined
                      color="primary"
                      class="quick-chemical-chip"
                      @click="selectQuickChemical(quick)"
                    >
                      {{ quick.name }} ({{ quick.un }})
                    </v-chip>
                  </v-chip-group>
                </div>

                <!-- Multiple Matches Table -->
                <div v-if="(filteredChemicals.length > 1 || searchFlagCode) && !selectedChemicalExactMatch" class="mt-3">
                  <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1 d-flex justify-space-between align-center">
                    <span>Hakutulokset ({{ filteredChemicals.length }} kpl):</span>
                    <span v-if="filteredChemicals.length > 50" class="caption grey--text">
                      Näytetään 50 ensimmäistä
                    </span>
                  </div>
                  <v-list dense class="elevation-1 rounded" style="max-height: 340px; overflow-y: auto">
                    <v-list-item-group v-model="selectedListIndex" color="primary">
                      <v-list-item
                        v-for="(item, idx) in filteredChemicals.slice(0, 50)"
                        :key="idx"
                        @click="selectChemical(item)"
                      >
                        <v-list-item-avatar
                          v-if="getFirstFlagImage(item)"
                          size="32"
                          tile
                          class="mr-2"
                        >
                          <img
                            :src="getFirstFlagImage(item)"
                            :alt="item.flags"
                            style="object-fit: contain; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.25));"
                          />
                        </v-list-item-avatar>
                        <v-list-item-avatar
                          v-else
                          size="32"
                          tile
                          class="mr-2 grey lighten-3 d-flex align-center justify-center rounded"
                        >
                          <v-icon small color="grey">mdi-flask-outline</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">
                            {{ item.name }}
                          </v-list-item-title>
                          <v-list-item-subtitle class="caption">
                            YK {{ item.unCode }} | Vaara: {{ item.dangerCode || "—" }}
                            <span v-if="item.flags" class="font-weight-medium grey--text text--darken-2 ml-1">
                              · Lipuke {{ formatFlagDisplay(item.flags) }}
                            </span>
                            <span v-if="item.tInstruction" class="error--text font-weight-bold ml-1">
                              [{{ item.tInstruction }}]
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Selected Chemical Info & TOKEVA Instruction -->
          <v-col cols="12" md="7" lg="8">
            <div v-if="selectedChemical">
              <!-- Chemical Header Card -->
              <v-card outlined class="mb-4">
                <v-card-title class="d-flex align-center justify-space-between flex-wrap py-2">
                  <div class="d-flex align-center flex-wrap">
                    <span class="text-h6 font-weight-bold mr-2 text-capitalize">
                      {{ selectedChemical.name }}
                    </span>
                    <v-chip color="orange darken-4" dark small class="font-weight-bold mr-1">
                      YK {{ selectedChemical.unCode }}
                    </v-chip>
                    <v-chip v-if="selectedChemical.dangerCode" color="grey darken-3" dark small class="font-weight-bold mr-1">
                      Kemler {{ selectedChemical.dangerCode }}
                    </v-chip>
                    <v-chip v-if="selectedChemical.tInstruction" color="error" small class="font-weight-bold">
                      Ohje {{ selectedChemical.tInstruction }}
                    </v-chip>
                  </div>
                  <div class="d-flex align-center my-1">
                    <v-btn
                      v-if="selectedChemical.ovaInstructionLink"
                      :href="selectedChemical.ovaInstructionLink"
                      target="_blank"
                      small
                      outlined
                      color="blue darken-2"
                      class="mr-2"
                    >
                      <v-icon left small>mdi-file-document</v-icon>
                      TTL OVA-ohje
                    </v-btn>
                    <v-btn
                      :href="getTokevaWebUrl(selectedChemical)"
                      target="_blank"
                      small
                      outlined
                      color="secondary"
                    >
                      <v-icon left small>mdi-open-in-new</v-icon>
                      Tokeva.fi
                    </v-btn>
                  </div>
                </v-card-title>

                <!-- Critical Warning: Water Reaction (X prefix) -->
                <v-alert
                  v-if="hasWaterReactionWarning(selectedChemical.dangerCode)"
                  type="error"
                  dense
                  prominent
                  class="ma-3"
                  icon="mdi-water-alert"
                >
                  <div class="font-weight-bold text-subtitle-2">
                    REAGOI VAARALLISESTI VEDEN KANSSA!
                  </div>
                  <div class="body-2">
                    Vaaran tunnusnumeron etuliite X tarkoittaa, että aine reagoi vaarallisesti veden kanssa.
                    Vettä saa käyttää ainoastaan asiantuntijan erillisellä hyväksynnällä!
                  </div>
                </v-alert>

                <v-card-text class="pt-2">
                  <!-- Hazard Description Text -->
                  <div v-if="getHazardDescription(selectedChemical.dangerCode)" class="mb-3 pa-2 rounded amber lighten-5 black--text body-2">
                    <strong>Vaaran tunnusnumero {{ selectedChemical.dangerCode }}:</strong>
                    {{ getHazardDescription(selectedChemical.dangerCode) }}
                  </div>

                  <!-- Details Grid -->
                  <v-row dense class="mb-2">
                    <v-col cols="6" sm="3">
                      <div class="text-body-2 grey--text text--darken-1">VAK-pääluokka</div>
                      <div class="text-body-1 font-weight-bold">{{ selectedChemical.dangerCategory || "—" }}</div>
                    </v-col>
                    <v-col cols="6" sm="3">
                      <div class="text-body-2 grey--text text--darken-1">Luokituskoodi</div>
                      <div class="text-body-1 font-weight-bold">{{ selectedChemical.classifyingCode || "—" }}</div>
                    </v-col>
                    <v-col cols="6" sm="3">
                      <div class="text-body-2 grey--text text--darken-1">Pakkausryhmä</div>
                      <div class="text-body-1 font-weight-bold">{{ selectedChemical.packagingClass || "—" }}</div>
                    </v-col>
                    <v-col cols="6" sm="3">
                      <div class="text-body-2 grey--text text--darken-1">Varoituslipuke</div>
                      <div class="d-flex align-center flex-wrap mt-1">
                        <template v-if="getFlagsForChemical(selectedChemical).length > 0">
                          <v-chip
                            v-for="(fItem, fIdx) in getFlagsForChemical(selectedChemical)"
                            :key="fIdx"
                            x-small
                            color="orange darken-3"
                            dark
                            class="font-weight-bold mr-1 mb-1 cursor-pointer"
                            @click="openFlagDialog(fItem)"
                          >
                            <v-avatar left v-if="fItem.images && fItem.images.length > 0" class="mr-1">
                              <img :src="`/img/tokeva/flags/image${fItem.images[0]}.png`" :alt="fItem.flag" />
                            </v-avatar>
                            {{ fItem.flag }}
                          </v-chip>
                        </template>
                        <div v-else class="text-body-1 font-weight-bold">{{ selectedChemical.flags || "—" }}</div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Warning Placards (Varoituslipukkeet) Visual Cards Section -->
                  <div v-if="getFlagsForChemical(selectedChemical).length > 0" class="mb-3">
                    <div class="caption font-weight-bold grey--text text--darken-2 mb-2 d-flex align-center">
                      <v-icon x-small left color="orange darken-3">mdi-alert-octagon</v-icon>
                      Varoituslipukkeet (ADR / VAK):
                    </div>
                    <v-row dense>
                      <v-col
                        v-for="(fItem, fIdx) in getFlagsForChemical(selectedChemical)"
                        :key="fIdx"
                        cols="12"
                        :sm="getFlagsForChemical(selectedChemical).length > 1 ? 6 : 12"
                      >
                        <v-card
                          outlined
                          hover
                          class="pa-2 d-flex align-center cursor-pointer placard-preview-card"
                          @click="openFlagDialog(fItem)"
                        >
                          <div class="d-flex align-center mr-3 flex-shrink-0">
                            <template v-if="fItem.images && fItem.images.length > 0">
                              <img
                                v-for="imgId in fItem.images"
                                :key="imgId"
                                :src="`/img/tokeva/flags/image${imgId}.png`"
                                :alt="fItem.flag"
                                class="placard-diamond-thumb"
                              />
                            </template>
                            <v-icon v-else size="44" color="grey">mdi-alert-outline</v-icon>
                          </div>
                          <div class="flex-grow-1 pr-1">
                            <div class="d-flex align-center justify-space-between flex-wrap">
                              <span class="font-weight-bold text-body-2">
                                Lipuke {{ fItem.flag }}
                              </span>
                              <v-chip x-small outlined color="primary">
                                <v-icon x-small left>mdi-magnify-plus</v-icon>
                                Avaa
                              </v-chip>
                            </div>
                            <div class="caption grey--text text--darken-2 text-truncate-2 mt-1">
                              {{ fItem.description }}
                            </div>
                          </div>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Danger Zone Dimensions (Eristysrajat) -->
                  <v-card outlined class="pa-3 mb-2 grey lighten-5">
                    <div class="font-weight-bold text-subtitle-1 mb-2 d-flex align-center">
                      <v-icon small left color="error">mdi-radius-outline</v-icon>
                      Vaara-alueen eristysmitat (TOKEVA):
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <div class="text-body-2 grey--text text--darken-2 font-weight-medium">Pieni vuoto / määrä:</div>
                        <div class="text-h5 font-weight-black red--text text--darken-2">
                          {{ selectedChemical.smallAmountDangerZoneDimensions || "—" }} m
                        </div>
                        <div class="text-body-2 grey--text text--darken-1">lähietäisyys / eristysalue</div>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <div class="text-body-2 grey--text text--darken-2 font-weight-medium">Suuri vuoto / määrä:</div>
                        <div class="text-h5 font-weight-black red--text text--darken-4">
                          {{ selectedChemical.largeAmountDangerZoneDimensions || "—" }} m
                        </div>
                        <div class="text-body-2 grey--text text--darken-1">lähietäisyys / eristysalue</div>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-card-text>
              </v-card>

              <!-- TOKEVA T-INSTRUCTION VIEWER (With Integrated Danger Zone) -->
              <div v-if="selectedInstruction">
                <tokeva-instruction-viewer
                  :instruction="selectedInstruction"
                  :chemical-id="selectedChemical.id"
                  :chemical-name="selectedChemical.name"
                  :un-code="selectedChemical.unCode"
                  :small-zone="selectedChemical.smallAmountDangerZoneDimensions"
                  :large-zone="selectedChemical.largeAmountDangerZoneDimensions"
                  @open-gas-guide="activeTab = 1"
                  @select-un="onSelectUnFromTable"
                />
              </div>
              <v-alert v-else type="info" outlined dense>
                Aineelle ei ole määritetty erillistä TOKEVA T-ohjetta tai ohjeen koodia ({{ selectedChemical.tInstruction }}) ei löytynyt.
              </v-alert>
            </div>

            <!-- Empty State -->
            <v-card v-else outlined class="pa-6 text-center">
              <v-icon size="64" color="grey lighten-1">mdi-hazard-lights</v-icon>
              <div class="text-h6 font-weight-medium grey--text text--darken-1 mt-2">
                Valitse kemikaali tai syötä VAK-kilven tiedot
              </div>
              <div class="body-2 grey--text mt-1">
                Voit hakea YK-numerolla (esim. 1830), aineen nimellä (esim. rikkihappo) tai ottaa kuvan oranssista kilvestä kameralla.
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 2: KAASUPULLOJEN VÄRIT (SFS-EN 1089-3) -->
      <v-tab-item>
        <gas-cylinder-guide @open-instruction="openInstructionFromGas" />
      </v-tab-item>

      <!-- TAB 3: KAIKKI TOKEVA T-TOIMINTAOHJEET (T1 - T9b, CBRNE) -->
      <v-tab-item>
        <v-row>
          <v-col cols="12" md="4" lg="3">
            <v-card outlined>
              <v-card-title class="py-2 text-subtitle-1 font-weight-bold">
                TOKEVA T-ohjeet ({{ allInstructionList.length }} kpl)
              </v-card-title>
              <v-card-text class="pt-1">
                <v-text-field
                  v-model="instructionFilter"
                  label="Suodata ohjeita"
                  placeholder="esim. T8a, kaasu, neste..."
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  class="mb-2"
                />
                <v-list dense style="max-height: 600px; overflow-y: auto">
                  <v-list-item-group v-model="selectedInstructionNavKey" color="primary">
                    <v-list-item
                      v-for="item in filteredInstructionList"
                      :key="item.key"
                      :value="item.key"
                      @click="browseInstruction(item.key)"
                    >
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">
                          <span class="error--text mr-1">[{{ item.code }}]</span>
                          {{ item.title }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="8" lg="9">
            <div v-if="browsedInstruction">
              <tokeva-instruction-viewer
                :instruction="browsedInstruction"
                @open-gas-guide="activeTab = 1"
                @select-un="onSelectUnFromTable"
              />
            </div>
            <v-card v-else outlined class="pa-6 text-center">
              <v-icon size="64" color="grey lighten-1">mdi-book-open-page-variant</v-icon>
              <div class="text-h6 font-weight-medium grey--text text--darken-1 mt-2">
                Valitse toimintaohje vasemmalta
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 3: VAARAN TUNNUSNUMEROT (KEMLER) -->
      <v-tab-item>
        <v-card outlined>
          <v-card-title class="py-2 text-subtitle-1 font-weight-bold d-flex justify-space-between align-center flex-wrap">
            <span>Oranssin kilven vaaran tunnusnumerot (Kemler-koodit)</span>
            <v-text-field
              v-model="hazardFilter"
              label="Etsi vaaranumerolla tai selityksellä"
              placeholder="esim. 33, 80, palava, vesi..."
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-magnify"
              hide-details
              style="max-width: 300px"
            />
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="hazardHeaders"
              :items="filteredHazards"
              :items-per-page="-1"
              :footer-props="{
                itemsPerPageOptions: [-1, 25, 50, 100],
                itemsPerPageAllText: 'Kaikki (98 kpl)',
              }"
              dense
              no-data-text="Ei vaaranumeroita hakuehdoilla"
            >
              <template v-slot:item.tunnus="{ item }">
                <v-chip
                  small
                  class="font-weight-bold"
                  :color="item.tunnus.startsWith('X') ? 'error' : 'orange darken-4'"
                  dark
                  @click="filterChemicalsByHazard(item.tunnus)"
                >
                  {{ item.tunnus }}
                </v-chip>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn
                  x-small
                  outlined
                  color="primary"
                  @click="filterChemicalsByHazard(item.tunnus)"
                >
                  Hae aineet
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- TAB 5: VAROITUSLIPUKKEET (ADR / VAK) -->
      <v-tab-item>
        <v-card outlined>
          <v-card-title class="py-3 d-flex align-center justify-space-between flex-wrap">
            <div>
              <div class="text-subtitle-1 font-weight-bold d-flex align-center">
                <v-icon left color="orange darken-3">mdi-alert-octagon</v-icon>
                ADR / VAK -varoituslipukkeet (vaaralipukkeet)
              </div>
              <div class="caption grey--text text--darken-1">
                Vaarallisten aineiden kuljetusluokkien viralliset varoituslipukkeet (VAK / ADR / GHS)
              </div>
            </div>
            <div class="d-flex align-center mt-2 mt-sm-0">
              <v-text-field
                v-model="flagSearchQuery"
                label="Etsi lipukkeen numerolla tai nimellä"
                placeholder="esim. 3, syövyttävä, palava, räjähde..."
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-magnify"
                hide-details
                style="min-width: 280px"
              />
            </div>
          </v-card-title>

          <!-- Category filter chips -->
          <v-card-text class="pt-0 pb-2">
            <v-chip-group v-model="selectedFlagCategory" column active-class="primary--text font-weight-bold">
              <v-chip
                v-for="cat in flagCategories"
                :key="cat.id"
                :value="cat.id"
                small
                filter
                outlined
              >
                {{ cat.name }}
              </v-chip>
            </v-chip-group>
          </v-card-text>

          <v-divider />

          <!-- Placards Grid -->
          <v-card-text class="pa-4">
            <v-row>
              <v-col
                v-for="flagItem in filteredFlagsCatalog"
                :key="flagItem.flag"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <v-card
                  outlined
                  hover
                  class="fill-height d-flex flex-column placard-catalog-card"
                >
                  <!-- Placard Image Container -->
                  <div
                    class="placard-img-wrapper pa-4 d-flex align-center justify-center cursor-pointer"
                    @click="openFlagDialog(flagItem)"
                  >
                    <template v-if="flagItem.images && flagItem.images.length > 0">
                      <div class="d-flex align-center justify-center flex-wrap" style="gap: 8px;">
                        <img
                          v-for="imgId in flagItem.images"
                          :key="imgId"
                          :src="`/img/tokeva/flags/image${imgId}.png`"
                          :alt="flagItem.flag"
                          class="placard-catalog-img"
                          title="Napsauta suurentaaksesi"
                        />
                      </div>
                    </template>
                    <div v-else class="text-center py-6 grey--text">
                      <v-icon size="48" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
                      <div class="caption mt-1">Ei erillistä kuvaa</div>
                    </div>
                  </div>

                  <v-divider />

                  <!-- Card Content -->
                  <v-card-text class="flex-grow-1 py-3">
                    <div class="d-flex align-center justify-space-between mb-1">
                      <v-chip small color="orange darken-3" dark class="font-weight-bold">
                        Lipuke {{ flagItem.flag }}
                      </v-chip>
                      <span class="caption grey--text text--darken-1 font-weight-medium">
                        {{ getFlagChemicalCount(flagItem.flag) }} ainetta
                      </span>
                    </div>
                    <div class="body-2 font-weight-medium text-capitalize-first mt-2">
                      {{ flagItem.description }}
                    </div>
                  </v-card-text>

                  <v-divider />

                  <!-- Card Actions -->
                  <v-card-actions class="px-3 py-2">
                    <v-btn
                      small
                      text
                      color="secondary"
                      @click="openFlagDialog(flagItem)"
                    >
                      <v-icon left small>mdi-arrow-expand</v-icon>
                      Suurenna
                    </v-btn>
                    <v-spacer />
                    <v-btn
                      small
                      color="primary"
                      depressed
                      :disabled="getFlagChemicalCount(flagItem.flag) === 0"
                      @click="filterChemicalsByFlag(flagItem.flag)"
                    >
                      <v-icon left small>mdi-magnify</v-icon>
                      Hae aineet
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>

            <!-- Empty state if search returns nothing -->
            <div v-if="filteredFlagsCatalog.length === 0" class="text-center py-8">
              <v-icon size="48" color="grey">mdi-alert-octagon-outline</v-icon>
              <div class="text-subtitle-1 grey--text text--darken-1 mt-2">
                Ei hakuehtoja vastaavia varoituslipukkeita
              </div>
            </div>

            <!-- Information alert about UNECE / ADR standards -->
            <v-alert
              outlined
              dense
              color="info"
              class="mt-6"
              icon="mdi-information-outline"
            >
              <div class="d-flex align-center justify-space-between flex-wrap">
                <div class="body-2">
                  VAK / ADR -varoituslipukkeet perustuvat YK:n vaarallisten aineiden kuljetussuosituksiin (UNECE Model Regulations / ADR-sopimus).
                </div>
                <v-btn
                  href="https://unece.org/transport/dangerous-goods/ghs-pictograms"
                  target="_blank"
                  small
                  text
                  color="primary"
                  class="mt-1 mt-sm-0"
                >
                  <v-icon left small>mdi-open-in-new</v-icon>
                  UNECE GHS / ADR -standardit
                </v-btn>
              </div>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- FLAG POPUP DIALOG (Matching media_1792278536832_c0ab3b3e.png) -->
    <v-dialog
      v-model="flagDialog"
      max-width="560"
      content-class="flag-dialog-modal"
    >
      <v-card v-if="selectedFlagDialogItem" class="flag-dialog-card">
        <v-card-title class="py-3 px-4 d-flex align-center justify-space-between grey lighten-4">
          <div class="d-flex align-center">
            <v-icon color="orange darken-3" class="mr-2">mdi-alert-octagon</v-icon>
            <span class="text-subtitle-1 font-weight-bold">
              Varoituslipuke {{ selectedFlagDialogItem.flag }}
            </span>
          </div>
          <v-btn icon small @click="flagDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6 pb-4 px-6 text-center">
          <!-- Image container matching user reference photo media_1792278536832_c0ab3b3e.png -->
          <div class="flag-dialog-image-box pa-4 mb-4 rounded-lg d-flex align-center justify-center flex-wrap">
            <template v-if="selectedFlagDialogItem.images && selectedFlagDialogItem.images.length > 0">
              <img
                v-for="imgId in selectedFlagDialogItem.images"
                :key="imgId"
                :src="`/img/tokeva/flags/image${imgId}.png`"
                :alt="selectedFlagDialogItem.flag"
                class="flag-dialog-img ma-2"
              />
            </template>
            <v-icon v-else size="100" color="grey">mdi-alert-octagon-outline</v-icon>
          </div>

          <!-- Description matching user screenshot text -->
          <div class="text-h6 font-weight-bold black--text mb-2">
            {{ selectedFlagDialogItem.description }}
          </div>

          <div class="body-2 grey--text text--darken-2 mb-2">
            Tietokannassa on <strong>{{ getFlagChemicalCount(selectedFlagDialogItem.flag) }}</strong> kemikaalia, joilla on tämä varoituslipuke.
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-3 grey lighten-4 d-flex align-center justify-space-between flex-wrap">
          <v-btn
            text
            color="grey darken-2"
            href="https://unece.org/transport/dangerous-goods/ghs-pictograms"
            target="_blank"
            small
          >
            <v-icon left x-small>mdi-open-in-new</v-icon>
            UNECE GHS / ADR
          </v-btn>
          <div class="d-flex align-center">
            <v-btn
              text
              class="mr-2"
              @click="flagDialog = false"
            >
              Sulje
            </v-btn>
            <v-btn
              color="primary"
              depressed
              :disabled="getFlagChemicalCount(selectedFlagDialogItem.flag) === 0"
              @click="filterChemicalsByFlag(selectedFlagDialogItem.flag)"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Hae tämän lipukkeen aineet ({{ getFlagChemicalCount(selectedFlagDialogItem.flag) }})
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage";
import TokevaInstructionViewer from "@/components/TokevaInstructionViewer.vue";
import GasCylinderGuide from "@/components/GasCylinderGuide.vue";
import api from "@/api";

// Lazy-loaded datasets from src/data/tokeva
let chemicalsData = null;
let hazardsData = null;
let hazardMapData = null;
let instructionsData = null;
let flagsData = null;

const STORAGE_KEY_LAST_CHEMICAL = "tokeva_last_chemical";

export default {
  name: "Kemikaalitiedot",
  components: {
    BasicPage,
    TokevaInstructionViewer,
    GasCylinderGuide,
  },
  data: () => ({
    activeTab: 0,
    loadingData: true,
    ocrLoading: false,

    // Search inputs
    searchQuery: "",
    searchDangerCode: "",
    searchUnCode: "",
    searchFlagCode: "",

    // Loaded data
    chemicals: [],
    hazards: [],
    hazardMap: {},
    instructions: {},
    flags: [],

    // Selected state
    selectedChemical: null,
    selectedChemicalExactMatch: false,
    selectedListIndex: null,

    // Tab 2 browsing
    instructionFilter: "",
    selectedInstructionNavKey: "t8a",
    browsedInstruction: null,

    // Tab 3 hazards table
    hazardFilter: "",
    hazardHeaders: [
      { text: "Vaaran tunnusnumero (Kemler)", value: "tunnus", width: "180px" },
      { text: "Vaaran merkitys ja kuvaus", value: "vaara" },
      { text: "Toiminnot", value: "actions", sortable: false, width: "120px" },
    ],

    // Tab 4 flags catalog
    flagSearchQuery: "",
    selectedFlagCategory: "all",
    flagCategories: [
      { id: "all", name: "Kaikki" },
      { id: "1", name: "Luokka 1: Räjähteet" },
      { id: "2", name: "Luokka 2: Kaasut" },
      { id: "3", name: "Luokka 3: Palavat nesteet" },
      { id: "4", name: "Luokka 4: Palavat kiinteät" },
      { id: "5", name: "Luokka 5: Hapettavat" },
      { id: "6", name: "Luokka 6: Myrkylliset" },
      { id: "7", name: "Luokka 7: Radioaktiiviset" },
      { id: "8", name: "Luokka 8: Syövyttävät" },
      { id: "9", name: "Luokka 9: Muut & Akut" },
    ],

    // Flag modal dialog
    flagDialog: false,
    selectedFlagDialogItem: null,

    quickChemicals: [
      { name: "Rikkihappo", un: "1830", kemler: "80" },
      { name: "Bensiini", un: "1203", kemler: "33" },
      { name: "Diesel", un: "1202", kemler: "30" },
      { name: "Nestekaasu", un: "1075", kemler: "23" },
      { name: "Ammoniakki", un: "1005", kemler: "268" },
      { name: "Asetyleeni", un: "1001", kemler: "239" },
      { name: "Happi", un: "1072", kemler: "25" },
      { name: "Typpi", un: "1066", kemler: "20" },
    ],
  }),
  computed: {
    selectedInstruction() {
      if (!this.selectedChemical?.tInstruction) return null;
      const key = this.normalizeInstructionKey(
        this.selectedChemical.tInstruction
      );
      return this.instructions[key] || null;
    },
    flagMap() {
      const map = {};
      for (const f of this.flags) {
        if (f.flag) {
          map[f.flag.trim()] = f;
        }
      }
      return map;
    },
    filteredFlagsCatalog() {
      let list = this.flags.filter((f) => f.images && f.images.length > 0);
      const cat = this.selectedFlagCategory;
      if (cat && cat !== "all") {
        list = list.filter((f) => {
          const fl = String(f.flag).trim();
          return fl.startsWith(cat);
        });
      }
      const q = (this.flagSearchQuery || "").trim().toLowerCase();
      if (q) {
        list = list.filter((f) => {
          const matchFlag = String(f.flag).toLowerCase().includes(q);
          const matchDesc = String(f.description).toLowerCase().includes(q);
          return matchFlag || matchDesc;
        });
      }
      return list;
    },
    filteredChemicals() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      const un = (this.searchUnCode || "").trim().toLowerCase();
      const dc = (this.searchDangerCode || "").trim().toUpperCase();
      const flagCode = (this.searchFlagCode || "").trim();

      // If user is searching via the text query field (name or UN code):
      if (q) {
        return this.chemicals.filter((chem) => {
          const matchName = chem.name?.toLowerCase().includes(q);
          const matchUn =
            chem.unCode?.toLowerCase() === q ||
            chem.unCode?.toLowerCase().includes(q);
          const matchDc = chem.dangerCode?.toLowerCase() === q;
          return matchName || matchUn || matchDc;
        });
      }

      // If user is searching via the orange placard inputs (Kemler / UN):
      if (un || dc) {
        return this.chemicals.filter((chem) => {
          if (un && !chem.unCode?.toLowerCase().includes(un)) {
            return false;
          }
          if (dc && !chem.dangerCode?.toUpperCase().includes(dc)) {
            return false;
          }
          return true;
        });
      }

      // If user filtered by a specific warning placard:
      if (flagCode) {
        return this.chemicals.filter((chem) => {
          if (!chem.flags) return false;
          const tokens = chem.flags
            .split(/[\r\n+]+/)
            .map((s) => s.trim())
            .filter(Boolean);
          return tokens.includes(flagCode);
        });
      }

      return [];
    },
    allInstructionList() {
      return Object.entries(this.instructions).map(([key, inst]) => {
        const title = inst.tilanteet?.[0]?.otsikko?.teksti || inst.ohje || key;
        return {
          key,
          code: inst.ohje || key.toUpperCase(),
          title,
        };
      });
    },
    filteredInstructionList() {
      const f = (this.instructionFilter || "").trim().toLowerCase();
      if (!f) return this.allInstructionList;
      return this.allInstructionList.filter(
        (i) =>
          i.code.toLowerCase().includes(f) || i.title.toLowerCase().includes(f)
      );
    },
    filteredHazards() {
      const f = (this.hazardFilter || "").trim().toLowerCase();
      if (!f) return this.hazards;
      return this.hazards.filter(
        (h) =>
          h.tunnus.toLowerCase().includes(f) || h.vaara.toLowerCase().includes(f)
      );
    },
  },
  async created() {
    await this.loadTokevaData();
    this.handleRouteParams();
  },
  methods: {
    async loadTokevaData() {
      this.loadingData = true;
      try {
        if (!chemicalsData) {
          const [chems, haz, hazMap, inst, flg] = await Promise.all([
            import("@/data/tokeva/chemicals.json"),
            import("@/data/tokeva/hazards.json"),
            import("@/data/tokeva/hazardMap.json"),
            import("@/data/tokeva/instructions.json"),
            import("@/data/tokeva/flags.json"),
          ]);
          chemicalsData = chems.default || chems;
          hazardsData = haz.default || haz;
          hazardMapData = hazMap.default || hazMap;
          instructionsData = inst.default || inst;
          flagsData = flg.default || flg;
        }
        this.chemicals = chemicalsData;
        this.hazards = hazardsData;
        this.hazardMap = hazardMapData;
        this.instructions = instructionsData;
        this.flags = flagsData || [];
        this.browsedInstruction =
          this.instructions[this.selectedInstructionNavKey] || null;
      } catch (err) {
        console.error("Failed to load TOKEVA data:", err);
        this.$toast("TOKEVA-tietokannan lataaminen epäonnistui", {
          type: "error",
        });
      } finally {
        this.loadingData = false;
      }
    },
    handleRouteParams() {
      const { id, un, t, kemler, kaasu, pullo, gas, lipuke, flag } =
        this.$route.query || {};

      if (kaasu || pullo || gas) {
        this.activeTab = 1;
        return;
      }
      if (lipuke || flag) {
        this.activeTab = 4;
        this.selectedFlagCategory = "all";
        this.flagSearchQuery = lipuke || flag;
        return;
      }
      if (id) {
        const found = this.chemicals.find((c) => c.id === id);
        if (found) {
          this.selectChemical(found, true);
          return;
        }
      }
      if (un) {
        this.searchUnCode = un;
        this.onPlacardInput();
        return;
      }
      if (kemler) {
        this.searchDangerCode = kemler;
        this.onPlacardInput();
        return;
      }
      if (t) {
        this.activeTab = 2; // T-ohjeet tab is now index 2
        this.browseInstruction(this.normalizeInstructionKey(t));
        return;
      }

      // Check localStorage for previously opened chemical / search
      const saved = this.loadSavedChemical();
      if (saved) {
        let found = null;
        if (saved.id) {
          found = this.chemicals.find((c) => c.id === saved.id);
        }
        if (!found && saved.unCode) {
          if (saved.dangerCode) {
            found = this.chemicals.find(
              (c) =>
                c.unCode === saved.unCode && c.dangerCode === saved.dangerCode
            );
          }
          if (!found) {
            found = this.chemicals.find((c) => c.unCode === saved.unCode);
          }
        }
        if (!found && saved.name) {
          found = this.chemicals.find(
            (c) => c.name?.toLowerCase() === saved.name?.toLowerCase()
          );
        }
        if (found) {
          if (saved.searchQuery) {
            this.searchQuery = saved.searchQuery;
          }
          this.selectChemical(found, true);
          return;
        } else if (saved.searchQuery) {
          this.searchQuery = saved.searchQuery;
          this.onTextSearchInput();
          return;
        }
      }

      // Default initial view: Rikkihappo UN 1830 (matches tokeva.fi sample)
      const defaultChem = this.chemicals.find((c) => c.unCode === "1830");
      if (defaultChem) {
        this.selectChemical(defaultChem, false);
      }
    },
    saveLastChemical(chem) {
      if (!chem) return;
      try {
        const payload = {
          id: chem.id || null,
          unCode: chem.unCode || null,
          dangerCode: chem.dangerCode || null,
          name: chem.name || null,
          searchQuery: this.searchQuery || "",
          timestamp: Date.now(),
        };
        localStorage.setItem(
          STORAGE_KEY_LAST_CHEMICAL,
          JSON.stringify(payload)
        );
      } catch (e) {
        console.warn("Failed to save last chemical to localStorage", e);
      }
    },
    saveSearchState() {
      try {
        if (!this.selectedChemical && !this.searchUnCode && !this.searchDangerCode && !this.searchQuery) {
          localStorage.removeItem(STORAGE_KEY_LAST_CHEMICAL);
          return;
        }
        const payload = {
          id: this.selectedChemical?.id || null,
          unCode: this.selectedChemical?.unCode || this.searchUnCode || null,
          dangerCode:
            this.selectedChemical?.dangerCode || this.searchDangerCode || null,
          name: this.selectedChemical?.name || null,
          searchQuery: this.searchQuery || "",
          timestamp: Date.now(),
        };
        localStorage.setItem(
          STORAGE_KEY_LAST_CHEMICAL,
          JSON.stringify(payload)
        );
      } catch (e) {
        console.warn("Failed to save search state to localStorage", e);
      }
    },
    loadSavedChemical() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY_LAST_CHEMICAL);
        if (!raw) return null;
        const data = JSON.parse(raw);
        return data || null;
      } catch (e) {
        console.warn("Failed to load last chemical from localStorage", e);
        return null;
      }
    },
    openInstructionFromGas(instructionCode) {
      this.activeTab = 2; // Switch to T-ohjeet tab
      this.browseInstruction(this.normalizeInstructionKey(instructionCode));
    },
    normalizeInstructionKey(code) {
      if (!code) return "";
      return String(code)
        .toLowerCase()
        .replace(/\*/g, "")
        .replace(/\./g, "_")
        .trim();
    },
    clearPlacard() {
      this.searchDangerCode = "";
      this.searchUnCode = "";
      this.selectedChemical = null;
      this.selectedChemicalExactMatch = false;
      this.saveSearchState();
    },
    onPlacardInput() {
      this.selectedChemicalExactMatch = false;
      this.searchFlagCode = "";
      // If user types directly in placard, clear the free text field to avoid stale query filter
      if (this.searchQuery) {
        this.searchQuery = "";
      }

      const un = (this.searchUnCode || "").trim().toLowerCase();
      const dc = (this.searchDangerCode || "").trim().toUpperCase();

      if (!un && !dc) {
        this.selectedChemical = null;
        this.saveSearchState();
        return;
      }

      // If UN code is not provided (user only typed Kemler danger code):
      // Kemler code alone (e.g. 80, 33) is a hazard category, not a specific chemical.
      // Do not auto-select any chemical so the placard is never modified.
      if (!un) {
        this.selectedChemical = null;
        this.saveSearchState();
        return;
      }

      const list = this.filteredChemicals;

      // Only auto-select if there is an exact UN match (4-digit code matching chem.unCode):
      const exactUnMatches = list.filter((c) => c.unCode?.toLowerCase() === un);

      if (exactUnMatches.length === 1) {
        const dcMatches = !dc || exactUnMatches[0].dangerCode?.toUpperCase() === dc;
        this.selectChemical(exactUnMatches[0], dcMatches, false);
      } else if (exactUnMatches.length > 1) {
        const exact = dc
          ? exactUnMatches.find((c) => c.dangerCode?.toUpperCase() === dc)
          : null;
        if (exact) {
          this.selectChemical(exact, true, false);
        } else {
          this.selectChemical(exactUnMatches[0], false, false);
        }
      } else {
        // Partial or unrecognized UN code (e.g. user typed "1", "18", "183"):
        this.selectedChemical = null;
        this.saveSearchState();
      }
    },
    onTextSearchInput() {
      this.selectedChemicalExactMatch = false;
      this.searchFlagCode = "";
      const q = (this.searchQuery || "").trim().toLowerCase();

      // 1. If text search query is empty, reset placard and selection
      if (!q) {
        this.searchDangerCode = "";
        this.searchUnCode = "";
        this.selectedChemical = null;
        this.selectedListIndex = null;
        this.saveSearchState();
        return;
      }

      // 2. Clear old placard values so stale numbers are immediately wiped
      this.searchDangerCode = "";
      this.searchUnCode = "";

      const list = this.filteredChemicals;

      // 3. Exact UN number match (e.g. user typed "1203" or "1830")
      const exactUnMatch = list.find((c) => c.unCode?.toLowerCase() === q);
      if (exactUnMatch) {
        const variantsCount = list.filter(
          (c) => c.unCode === exactUnMatch.unCode
        ).length;
        this.selectChemical(exactUnMatch, variantsCount <= 1);
        return;
      }

      // 4. Exact chemical name match (e.g. user typed "bensiini" or "kloori")
      const exactNameMatch = list.find((c) => c.name?.toLowerCase() === q);
      if (exactNameMatch) {
        this.selectChemical(exactNameMatch, true);
        return;
      }

      // 4b. Starts with chemical name match (e.g. user typed "asetyleeni", matching "asetyleeni, liuotettu (etiini, etyyni)")
      const startsWithMatch = list.find(
        (c) =>
          c.name?.toLowerCase().startsWith(q + ",") ||
          c.name?.toLowerCase().startsWith(q + " ") ||
          c.name?.toLowerCase().startsWith(q + " (")
      );
      if (startsWithMatch) {
        this.selectChemical(startsWithMatch, list.length === 1);
        return;
      }

      // 5. Exactly 1 matching chemical
      if (list.length === 1) {
        this.selectChemical(list[0], true);
        return;
      }

      // 6. Multiple matches:
      if (list.length > 1) {
        // If all matching items have the identical UN code (e.g. diesel 1202 variants):
        const firstUn = list[0].unCode;
        const allSameUn = list.every((c) => c.unCode === firstUn);
        if (allSameUn) {
          this.selectChemical(list[0], false);
        } else {
          // Different substances match (e.g. user typed "happo"):
          // Keep placard inputs empty until a chemical is chosen, show results list
          this.selectedChemical = null;
          this.saveSearchState();
        }
      } else {
        this.selectedChemical = null;
        this.saveSearchState();
      }
    },
    selectChemical(chem, exact = true, updatePlacard = true) {
      this.selectedChemical = chem;
      this.selectedChemicalExactMatch = exact;
      if (updatePlacard) {
        this.searchUnCode = chem.unCode || "";
        this.searchDangerCode = chem.dangerCode || "";
      }
      this.saveLastChemical(chem);
    },
    selectQuickChemical(quick) {
      this.searchFlagCode = "";
      this.searchQuery = "";
      this.searchUnCode = quick.un;
      this.searchDangerCode = quick.kemler || "";
      const found = this.chemicals.find((c) => c.unCode === quick.un);
      if (found) {
        this.selectChemical(found, true);
      } else {
        this.onPlacardInput();
      }
    },
    resetSearch() {
      this.searchFlagCode = "";
      this.searchQuery = "";
      this.searchDangerCode = "";
      this.searchUnCode = "";
      this.selectedChemical = null;
      this.selectedChemicalExactMatch = false;
      try {
        localStorage.removeItem(STORAGE_KEY_LAST_CHEMICAL);
      } catch (e) {
        // ignore
      }
    },
    hasWaterReactionWarning(code) {
      return (code || "").toUpperCase().startsWith("X");
    },
    getHazardDescription(code) {
      if (!code) return "";
      return this.hazardMap[code] || "";
    },
    getTokevaWebUrl(chem) {
      if (!chem?.tInstruction) return "https://tokeva.fi";
      const code = chem.tInstruction
        .toLowerCase()
        .replace(/\*/g, "")
        .replace(/\./g, "")
        .trim();
      let url = `https://tokeva.fi/#/t-ohjeet/tohje/${code}`;
      if (chem.id) {
        url += `;id=${encodeURIComponent(chem.id)}`;
      }
      return url;
    },
    browseInstruction(key) {
      this.selectedInstructionNavKey = key;
      this.browsedInstruction = this.instructions[key] || null;
    },
    filterChemicalsByHazard(kemler) {
      this.activeTab = 0;
      this.searchFlagCode = "";
      this.searchDangerCode = kemler;
      this.searchUnCode = "";
      this.searchQuery = "";
      this.onPlacardInput();
    },
    getFlagsForChemical(chem) {
      if (!chem || !chem.flags) return [];
      const tokens = String(chem.flags)
        .split(/[\r\n+]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      return tokens.map((token) => {
        return (
          this.flagMap[token] || {
            flag: token,
            description: `Lipuke ${token}`,
            images: [],
          }
        );
      });
    },
    getFirstFlagImage(chem) {
      const flags = this.getFlagsForChemical(chem);
      for (const f of flags) {
        if (f.images && f.images.length > 0) {
          return `/img/tokeva/flags/image${f.images[0]}.png`;
        }
      }
      return null;
    },
    formatFlagDisplay(flagsStr) {
      if (!flagsStr) return "";
      return flagsStr
        .split(/[\r\n+]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .join(" + ");
    },
    getFlagChemicalCount(flagCode) {
      if (!flagCode || !this.chemicals) return 0;
      const target = String(flagCode).trim();
      let count = 0;
      for (const chem of this.chemicals) {
        if (!chem.flags) continue;
        const tokens = chem.flags
          .split(/[\r\n+]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (tokens.includes(target)) {
          count++;
        }
      }
      return count;
    },
    openFlagDialog(flagItem) {
      if (!flagItem) return;
      this.selectedFlagDialogItem = flagItem;
      this.flagDialog = true;
    },
    filterChemicalsByFlag(flagCode) {
      this.flagDialog = false;
      this.activeTab = 0;
      this.searchFlagCode = flagCode;
      this.searchQuery = "";
      this.searchDangerCode = "";
      this.searchUnCode = "";
      this.selectedChemicalExactMatch = false;

      const list = this.filteredChemicals;
      if (list.length > 0) {
        this.selectChemical(list[0], false);
      } else {
        this.selectedChemical = null;
      }
    },
    clearFlagFilter() {
      this.searchFlagCode = "";
      this.resetSearch();
    },
    async onVakImageCaptured(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      this.ocrLoading = true;
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result;
            const b64 = String(dataUrl).split(",")[1];
            resolve(b64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const mimeType = file.type || "image/jpeg";
        const result = (
          await api.recognizeVakPlate({
            imageBase64: base64,
            mimeType,
          })
        ).data;

        if (result.success && (result.unNumber || result.dangerCode)) {
          if (result.unNumber) this.searchUnCode = result.unNumber;
          if (result.dangerCode) this.searchDangerCode = result.dangerCode;
          this.searchQuery = "";
          this.onPlacardInput();
          this.$toast(
            `VAK-kilpi tunnistettu: Kemler ${result.dangerCode || "—"} / YK ${result.unNumber || "—"}`,
            { type: "success" }
          );
        } else {
          this.$toast("Kilven numeroita ei tunnistettu selkeästi kuvasta", {
            type: "warning",
          });
        }
      } catch (err) {
        console.error("VAK OCR error:", err);
        this.$toast("Virhe kuvan tunnistuksessa: " + (err.message || err), {
          type: "error",
        });
      } finally {
        this.ocrLoading = false;
        if (this.$refs.vakCameraInput) {
          this.$refs.vakCameraInput.value = "";
        }
      }
    },
    onSelectUnFromTable(un) {
      if (!un) return;
      this.activeTab = 0;
      this.searchUnCode = un;
      this.searchDangerCode = "";
      this.searchQuery = "";
      this.searchFlagCode = "";
      this.onPlacardInput();
    },
  },
};
</script>

<style scoped lang="scss">
.vak-placard-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.vak-placard {
  width: 100%;
  max-width: 320px;
  background-color: #ff6f00; /* Vibrant ADR orange */
  border: 5px solid #111111;
  border-radius: 6px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
  overflow: hidden;

  .vak-row {
    padding: 10px 8px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .vak-divider {
    height: 4px;
    background-color: #111111;
    width: 100%;
  }

  .vak-input {
    width: 100%;
    text-align: center;
    font-size: 1.5rem;
    font-family: monospace, monospace;
    letter-spacing: 2px;
    color: #111111;
    background: transparent;
    border: none;
    outline: none;

    &::placeholder {
      color: rgba(0, 0, 0, 0.5);
      font-size: 0.95rem;
      letter-spacing: 0;
      font-family: inherit;
    }

    &:focus {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 4px;
    }
  }
}

.transparent-tabs-items {
  background-color: transparent !important;
}

// Eliminate Vuetify 2 mobile tabs phantom 52px left margin
::v-deep .v-slide-group:not(.v-slide-group--has-affixes) {
  > .v-slide-group__prev,
  > .v-slide-group__next {
    display: none !important;
  }
}

::v-deep .v-tabs:not(.v-tabs--vertical) {
  > .v-slide-group--is-overflowing.v-tabs-bar--is-mobile:not(.v-slide-group--has-affixes) {
    .v-slide-group__prev,
    .v-slide-group__next {
      display: none !important;
    }
  }
}

@media (max-width: 600px) {
  ::v-deep .v-slide-group__prev.v-slide-group__prev--disabled {
    display: none !important;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.placard-diamond-thumb {
  width: 48px;
  height: 48px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.12);
  }
}

.placard-preview-card {
  transition: all 0.2s ease;
  border-left: 4px solid #ff6f00 !important;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  }
}

.placard-catalog-card {
  transition: all 0.2s ease;
  border-radius: 8px !important;
  overflow: hidden;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
    border-color: #ff6f00 !important;
  }

  .placard-img-wrapper {
    background: #f8f9fa;
    min-height: 160px;
  }

  .placard-catalog-img {
    width: 105px;
    height: 105px;
    object-fit: contain;
    filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.25));
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.08);
    }
  }
}

.quick-chemical-chip {
  font-size: 13.5px !important;
  font-weight: 600 !important;
  height: 30px !important;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background-color: rgba(25, 118, 210, 0.08) !important;
  }
}

.flag-dialog-image-box {
  background: #ebebeb;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.08);

  .flag-dialog-img {
    max-width: 200px;
    max-height: 200px;
    object-fit: contain;
    filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.25));
  }
}

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.text-capitalize-first {
  display: inline-block;
  &::first-letter {
    text-transform: uppercase;
  }
}

.theme--dark {
  .placard-catalog-card .placard-img-wrapper {
    background: #2a2a2a;
  }
  .flag-dialog-image-box {
    background: #2a2a2a;
  }
  .flag-dialog-card .v-card__title,
  .flag-dialog-card .v-card__actions {
    background: #1e1e1e !important;
  }
}
</style>

